import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlacesAutocomplete } from '../hooks/usePlacesAutocomplete';
import { useRecentSearches } from '../hooks/useRecentSearches';
import AuthBar from '../components/Auth/AuthBar';
import SupportBanner from '../components/SupportBanner/SupportBanner';
import './Home.css';

/** Bump when dwellia-logo.png is updated to bust cache */
const LOGO_VERSION = 3;
/** Show sticky bar only after scrolling past the in-page hero search (not a fixed px) */
const STICKY_OFFSET = 20;

/** Scroll position that works on iOS and desktop (document vs window) */
function getScrollTop() {
  if (typeof window === 'undefined') return 0;
  const w = window.scrollY;
  const d = document.documentElement.scrollTop;
  const b = document.body.scrollTop;
  return Math.max(w, d, b, 0);
}

function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null); // 'hero' | 'sticky' | null
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [validationError, setValidationError] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const searchWrapperRef = useRef(null);
  const heroSearchFormRef = useRef(null);
  const stickySearchWrapperRef = useRef(null);
  const listRef = useRef(null);
  const stickyListRef = useRef(null);
  const heroInputRef = useRef(null);
  const stickyInputRef = useRef(null);
  const lastFocusedSearchRef = useRef(null); // 'hero' | 'sticky' – used to restore focus if browser blurs on dropdown update

  const searchAc = usePlacesAutocomplete(searchQuery, { prioritizeHealthcare: true });
  const loading = searchAc.loading;
  const { addRecentSearch, removeRecentSearch, getFilteredRecent } = useRecentSearches();

  const recentMatches = useMemo(() => getFilteredRecent(searchQuery), [searchQuery, getFilteredRecent]);
  const suggestions = useMemo(() => {
    const recentDescs = new Set(recentMatches.map((r) => r.description.toLowerCase()));
    const fresh = searchAc.suggestions.filter((s) => !recentDescs.has(s.description.toLowerCase()));
    return [...recentMatches, ...fresh];
  }, [recentMatches, searchAc.suggestions]);

  const showHeroDropdown =
    activeDropdown === 'hero' && (suggestions.length > 0 || (searchAc.isReady && loading));
  const showStickyDropdown =
    activeDropdown === 'sticky' && (suggestions.length > 0 || (searchAc.isReady && loading));

  useEffect(() => {
    let rafId = null;
    function updateScrolled() {
      const el = heroSearchFormRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        setScrolled(rect.bottom < STICKY_OFFSET);
      } else {
        setScrolled(getScrollTop() > 80);
      }
    }
    function onScroll() {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScrolled);
    }
    updateScrolled();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true });
    document.documentElement.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateScrolled);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll);
      document.documentElement.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateScrolled);
    };
  }, []);

  useLayoutEffect(() => {
    document.body.classList.add('page-home');
    return () => document.body.classList.remove('page-home');
  }, []);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        searchWrapperRef.current?.contains(e.target) ||
        stickySearchWrapperRef.current?.contains(e.target)
      )
        return;
      setActiveDropdown(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const listEl = activeDropdown === 'sticky' ? stickyListRef.current : listRef.current;
    if (highlightedIndex >= 0 && listEl) {
      const item = listEl.children[highlightedIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, activeDropdown]);

  // Restore focus when the browser blurs our input after dropdown/suggestions update (common on mobile and sometimes desktop).
  useLayoutEffect(() => {
    if (!(showHeroDropdown || showStickyDropdown) || !lastFocusedSearchRef.current) return;
    const active = document.activeElement;
    const hero = heroInputRef.current;
    const sticky = stickyInputRef.current;
    const isOurInput = active === hero || active === sticky;
    if (!isOurInput && (hero || sticky)) {
      const toFocus = lastFocusedSearchRef.current === 'sticky' ? sticky : hero;
      toFocus?.focus();
    }
  }, [showHeroDropdown, showStickyDropdown, suggestions, loading]);

  const runSearchWithQuery = (query) => {
    setValidationError('');
    const trimmed = (query || '').trim();
    if (!trimmed) {
      setValidationError('Please enter a work address, city, or ZIP code.');
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmed)}`, {
      state: {
        workingAddress: trimmed,
        stayingAddress: '',
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearchWithQuery(searchQuery);
  };

  const handleSelectSuggestion = (prediction) => {
    const pred = typeof prediction === 'string' ? { description: prediction } : prediction;
    const picked = pred.description || '';
    setSearchQuery(picked);
    setHighlightedIndex(-1);
    setActiveDropdown(null);
    searchAc.resetSession();
    addRecentSearch(pred);
    // Selecting autocomplete should search immediately.
    runSearchWithQuery(picked);
  };

  const showDropdown = showHeroDropdown || showStickyDropdown;
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === 'Escape') setActiveDropdown(null);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
        break;
      case 'Enter': {
        e.preventDefault();
        const selected =
          (highlightedIndex >= 0 && suggestions[highlightedIndex])
            ? suggestions[highlightedIndex]
            : suggestions[0];
        if (selected?.description) {
          handleSelectSuggestion(selected);
        } else {
          runSearchWithQuery(searchQuery);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setActiveDropdown(null);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const searchInputProps = (source) => ({
    type: 'text',
    className: `address-form__input home__search-input ${loading ? 'address-form__input--loading' : ''}`,
    placeholder: 'Enter a work address, city, state, or ZIP code',
    value: searchQuery,
    onChange: (e) => {
      setSearchQuery(e.target.value);
      setActiveDropdown(source);
    },
    onFocus: () => {
      setActiveDropdown(source);
      lastFocusedSearchRef.current = source;
    },
    onKeyDown: handleKeyDown,
    autoComplete: 'off',
    'aria-label': 'Search by address, neighborhood, city, or ZIP code',
    'aria-autocomplete': 'list',
    'aria-expanded': source === 'hero' ? showHeroDropdown : showStickyDropdown,
    'aria-controls': `home-search-suggestions-${source}`,
    'aria-activedescendant':
      highlightedIndex >= 0 && suggestions[highlightedIndex]
        ? `home-suggestion-${source}-${highlightedIndex}`
        : undefined,
  });

  const renderSuggestions = (source) => {
    const show = source === 'hero' ? showHeroDropdown : showStickyDropdown;
    const listRefToUse = source === 'hero' ? listRef : stickyListRef;
    if (!show) return null;
    return (
      <ul
        id={`home-search-suggestions-${source}`}
        className="address-form__suggestions home__search-suggestions"
        ref={listRefToUse}
        role="listbox"
      >
        {loading && suggestions.length === 0 ? (
          <li className="address-form__suggestion address-form__suggestion--loading">Searching…</li>
        ) : (
          suggestions.map((pred, i) => (
            <li
              key={pred.place_id}
              id={`home-suggestion-${source}-${i}`}
              className={`address-form__suggestion ${i === highlightedIndex ? 'address-form__suggestion--highlighted' : ''}`}
              role="option"
              aria-selected={i === highlightedIndex}
              onMouseEnter={() => setHighlightedIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectSuggestion(pred);
              }}
            >
              <span className="address-form__suggestion-row">
                <span className="address-form__suggestion-row-main">
                  {pred.isRecent && <span className="address-form__suggestion-recent-icon" aria-hidden="true">↺</span>}
                  {pred.structured_formatting?.main_text
                    ? <span className="address-form__suggestion-main">{pred.structured_formatting.main_text}</span>
                    : <span className="address-form__suggestion-main">{pred.description}</span>}
                </span>
                {pred.isRecent && (
                  <button
                    type="button"
                    className="address-form__suggestion-remove"
                    aria-label={`Remove recent search ${pred.description}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeRecentSearch(pred);
                      setHighlightedIndex(-1);
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
              {pred.structured_formatting?.secondary_text && (
                <span className="address-form__suggestion-secondary">
                  {pred.structured_formatting.secondary_text}
                </span>
              )}
            </li>
          ))
        )}
      </ul>
    );
  };

  return (
    <div className={`home ${scrolled ? 'home--sticky-visible' : ''}`}>
      {/* Sticky bar (visible on scroll) */}
      <div className={`home__sticky ${scrolled ? 'home__sticky--visible' : ''}`} aria-hidden={!scrolled}>
        <div className="home__sticky-inner">
          <div className="home__sticky-logo">
            <Link to="/" aria-hidden="true">
              <img src={`/dwellia-logo.png?v=${LOGO_VERSION}`} alt="" className="home__logo" />
            </Link>
          </div>
          <form className="home__sticky-search-form" onSubmit={handleSubmit}>
            <div
              className="home__search-wrap address-form__row--with-autocomplete home__sticky-search-wrap"
              ref={stickySearchWrapperRef}
            >
              <div className="address-form__input-wrap home__search-input-wrap">
                <input id="home-search-sticky" ref={stickyInputRef} {...searchInputProps('sticky')} />
                {loading && <span className="address-form__spinner" aria-hidden="true" />}
                <button type="submit" className="home__search-icon-btn" aria-label="Search">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </button>
                {renderSuggestions('sticky')}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Top bar: desktop = white banner; mobile = overlay on image (no banner strip) */}
      <div className="home__top-bar">
        <AuthBar variant="banner">
          <Link to="/" className="home__brand">
            <img src={`/dwellia-logo.png?v=${LOGO_VERSION}`} alt="Dwellia" className="home__logo" />
          </Link>
        </AuthBar>
      </div>

      <header className="home__header home__header--hero" style={{ backgroundImage: 'url(/HomeBanner.jpg)' }}>
        <div className="home__hero-content">
          <p className="home__tagline">Professional Healthcare Made Easy.</p>
          <form className="home__search-form" ref={heroSearchFormRef} onSubmit={handleSubmit}>
            <div
              className="home__search-wrap address-form__row--with-autocomplete"
              ref={searchWrapperRef}
            >
              <div className="address-form__input-wrap home__search-input-wrap">
                <input id="home-search" ref={heroInputRef} {...searchInputProps('hero')} />
                {loading && <span className="address-form__spinner" aria-hidden="true" />}
                <button type="submit" className="home__search-icon-btn" aria-label="Search">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </button>
                {renderSuggestions('hero')}
              </div>
            </div>
            {validationError && (
              <p className="address-form__error home__search-error" role="alert">
                {validationError}
              </p>
            )}
          </form>
        </div>
      </header>
      <main className="home__main">

        {/* ── How It Works ── */}
        <section className="home__features" aria-label="How it works">
          <h2 className="home__section-title">How It Works</h2>
          <div className="home__features-grid">

            <article className="home__feature-card">
              <div className="home__feature-icon-wrap" aria-hidden="true">
                {/* Magnifying glass */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
              <h3 className="home__feature-title">Search Any Area</h3>
              <p className="home__feature-desc">
                Enter a city, neighborhood, ZIP code, or address to delve into a new area.
              </p>
            </article>

            <article className="home__feature-card">
              <div className="home__feature-icon-wrap" aria-hidden="true">
                {/* Map pin */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h3 className="home__feature-title">Explore Like a Local</h3>
              <p className="home__feature-desc">
                View insights like cost of living, safety and more.
              </p>
            </article>

            <article className="home__feature-card">
              <div className="home__feature-icon-wrap" aria-hidden="true">
                {/* Layers / compare */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 2 7l10 5 10-5-10-5Z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h3 className="home__feature-title">Stack &amp; Decide</h3>
              <p className="home__feature-desc">
                Save favorites and stay closer to the places you enjoy most.
              </p>
            </article>

          </div>
        </section>

        {/* ── Why Dwellia ── */}
        <section className="home__highlights" aria-label="Why Dwellia">
          <div className="home__highlights-inner">
            <h2 className="home__section-title">Why Travelers Love Dwellia</h2>
            <ul className="home__highlights-list">

              <li className="home__highlight-item">
                <span className="home__highlight-check" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div className="home__highlight-text">
                  <strong>Feels Like Home</strong>
                  <p>Know your favorite spots before you arrive with favorites.</p>
                </div>
              </li>

              <li className="home__highlight-item">
                <span className="home__highlight-check" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div className="home__highlight-text">
                  <strong>Local Data &amp; Insights</strong>
                  <p>View safety, cost of living, and discover fun things to do!</p>
                </div>
              </li>

              <li className="home__highlight-item">
                <span className="home__highlight-check" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div className="home__highlight-text">
                  <strong>Save Time</strong>
                  <p>Enter your work address and find everything you need, fast.</p>
                </div>
              </li>

              <li className="home__highlight-item">
                <span className="home__highlight-check" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div className="home__highlight-text">
                  <strong>Interactive Map View</strong>
                  <p>Visualize every result on and filter by what matters most.</p>
                </div>
              </li>

            </ul>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="home__cta" aria-label="Get started">
          <h2 className="home__cta-title">Ready to Find Your Next Dwelling?</h2>
          <p className="home__cta-subtitle">
            Search a city or ZIP code above to get started — it’s free.
          </p>
          <a href="#home-search" className="home__cta-link" onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("home-search");
            if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
          }}>
            Start Searching
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </section>

      </main>
      <footer className="home__footer">
        <SupportBanner />
        <div className="home__footer-links">
          <Link to="/privacy" className="home__footer-link">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}

export default Home;
